import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Label } from '@/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Building2,
  Users,
  Globe,
  Phone,
  DollarSign,
  Target,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const Accounts = () => {const { t } = useTranslation("crm");
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    contact_type: 'prospect'
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/contacts`;
      const params = [];
      if (typeFilter && typeFilter !== 'all') {
        params.push(`contact_type=${typeFilter}`);
      }
      if (searchTerm) {
        params.push(`search=${searchTerm}`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch contacts');
      const data = await response.json();
      setContacts(data);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [typeFilter]);

  const updateNewContact = (field, value) => {
    setNewContact((current) => ({ ...current, [field]: value }));
  };

  const resetNewContact = () => {
    setNewContact({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      job_title: '',
      contact_type: 'prospect'
    });
  };

  const handleCreateContact = async (event) => {
    event.preventDefault();
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    setIsSavingContact(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newContact)
      });
      if (!response.ok) throw new Error('Failed to create contact');
      toast.success('Contact created');
      setIsAddContactOpen(false);
      resetNewContact();
      await fetchContacts();
    } catch (err) {
      toast.error('Failed to create contact');
    } finally {
      setIsSavingContact(false);
    }
  };

  const filteredAccounts = contacts.filter((account) => {
    if (!searchTerm) return true;
    return account.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.company?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: contacts.length,
    customers: contacts.filter((a) => a.contact_type === 'customer').length,
    prospects: contacts.filter((a) => a.contact_type === 'prospect').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-accounts">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("contacts", "Contacts")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageCustomerAnd", "Manage customer and prospect contacts")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContacts} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setIsAddContactOpen(true)}>
            <Plus className="h-4 w-4" />{t("addContact", "Add Contact")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalContacts", "Total Contacts")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("customers", "Customers")}</p>
                <p className="text-2xl font-bold">{stats.customers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Target className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("prospects", "Prospects")}</p>
                <p className="text-2xl font-bold">{stats.prospects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("partners", "Partners")}</p>
                <p className="text-2xl font-bold">{contacts.filter((c) => c.contact_type === 'partner').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts by name or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <div className="flex gap-2">
              {['all', 'customer', 'prospect', 'partner', 'vendor'].map((type) =>
              <Button
                key={type}
                variant={typeFilter === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter(type)}
                className={typeFilter === type ? 'bg-teal-600 hover:bg-teal-700' : ''}>
                
                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredAccounts.map((contact) =>
        <Card key={contact.id} className="card-elevated hover:shadow-lg transition-all cursor-pointer">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-teal-500/10 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{contact.first_name} {contact.last_name}</h3>
                    <p className="text-sm text-muted-foreground">{contact.company}</p>
                  </div>
                </div>
                <Badge variant={contact.contact_type === 'customer' ? 'default' : 'secondary'}
              className={contact.contact_type === 'customer' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' : ''}>
                  {contact.contact_type}
                </Badge>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-teal-600">{contact.email}</span>
                </div>
                {contact.phone &&
              <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{contact.phone}</span>
                  </div>
              }
                {contact.job_title &&
              <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("title", "Title:")}</span>
                    <span>{contact.job_title}</span>
                  </div>
              }
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex flex-wrap gap-1">
                  {contact.tags?.map((tag, idx) =>
                <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                )}
                </div>
                <Button variant="outline" size="sm">{t("viewDetails", "View Details")}</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {filteredAccounts.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("noContactsFound", "No contacts found")}</h3>
            <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
          </CardContent>
        </Card>
      }
      <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("addContact", "Add Contact")}</DialogTitle>
            <DialogDescription>
              {t("createCustomerOr", "Create a customer, prospect, partner, or vendor contact for CRM follow-up.")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateContact} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-first-name">{t("firstName", "First Name")}</Label>
                <Input
                  id="contact-first-name"
                  value={newContact.first_name}
                  onChange={(event) => updateNewContact('first_name', event.target.value)}
                  placeholder="Jane" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-last-name">{t("lastName", "Last Name")}</Label>
                <Input
                  id="contact-last-name"
                  value={newContact.last_name}
                  onChange={(event) => updateNewContact('last_name', event.target.value)}
                  placeholder="Smith" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">{t("email", "Email")}</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={newContact.email}
                  onChange={(event) => updateNewContact('email', event.target.value)}
                  placeholder="jane@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-phone">{t("phone", "Phone")}</Label>
                <Input
                  id="contact-phone"
                  value={newContact.phone}
                  onChange={(event) => updateNewContact('phone', event.target.value)}
                  placeholder="+1 555 0100" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-company">{t("company", "Company")}</Label>
                <Input
                  id="contact-company"
                  value={newContact.company}
                  onChange={(event) => updateNewContact('company', event.target.value)}
                  placeholder="Acme Corp" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-job-title">{t("jobTitle", "Job Title")}</Label>
                <Input
                  id="contact-job-title"
                  value={newContact.job_title}
                  onChange={(event) => updateNewContact('job_title', event.target.value)}
                  placeholder="Operations Director" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="contact-type">{t("contactType", "Contact Type")}</Label>
                <select
                  id="contact-type"
                  value={newContact.contact_type}
                  onChange={(event) => updateNewContact('contact_type', event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                  <option value="prospect">{t("prospect", "Prospect")}</option>
                  <option value="customer">{t("customer", "Customer")}</option>
                  <option value="partner">{t("partner", "Partner")}</option>
                  <option value="vendor">{t("vendor", "Vendor")}</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddContactOpen(false)}>
                {t("cancel", "Cancel")}
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={isSavingContact}>
                {isSavingContact ? t("saving", "Saving...") : t("createContact", "Create Contact")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>);

};
